import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { mealPlans } from '../../api/client'
import PageHeader from '../../components/PageHeader'
import { PageLayout, Card, SkeletonCard, EmptyState } from '../../components/ui'

const TYPE_COLORS = {
  balanced: { bg: 'bg-green-900/30', text: 'text-green-400', border: 'border-green-800/40' },
  low_carb: { bg: 'bg-blue-900/30', text: 'text-blue-400', border: 'border-blue-800/40' },
  high_protein: { bg: 'bg-red-900/30', text: 'text-red-400', border: 'border-red-800/40' },
  keto: { bg: 'bg-purple-900/30', text: 'text-purple-400', border: 'border-purple-800/40' },
  vegetarian: { bg: 'bg-teal-900/30', text: 'text-teal-400', border: 'border-teal-800/40' },
  vegan: { bg: 'bg-emerald-900/30', text: 'text-emerald-400', border: 'border-emerald-800/40' },
  paleo: { bg: 'bg-orange-900/30', text: 'text-orange-400', border: 'border-orange-800/40' },
  custom: { bg: 'bg-dial-elevated', text: 'text-indices-medium', border: 'border-dial-border' },
}

function MealPlanCard({ plan }) {
  const typeStyle = TYPE_COLORS[plan.mealPlanType] || TYPE_COLORS.custom
  const mealCount = plan.meals?.length || 0

  return (
    <Link to={`/meals/${plan.id}`}>
      <Card hover>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-medium text-indices-bright">{plan.name}</h3>
            {plan.description && (
              <p className="text-sm text-indices-medium mt-1 line-clamp-2">{plan.description}</p>
            )}
          </div>
          <span className={`px-2 py-1 text-xs rounded-full capitalize border ${typeStyle.bg} ${typeStyle.text} ${typeStyle.border}`}>
            {plan.mealPlanType?.replace('_', ' ')}
          </span>
        </div>
        <div className="flex items-center gap-4 mt-3 text-sm text-indices-muted">
          <span>{mealCount} {mealCount === 1 ? 'meal' : 'meals'}</span>
          {plan.totalCalories && (
            <span>{plan.totalCalories} cal</span>
          )}
          {plan.totalProteinG && (
            <span>{plan.totalProteinG}g protein</span>
          )}
        </div>
      </Card>
    </Link>
  )
}

export default function MealPlanListPage() {
  const { data: planList, isLoading } = useQuery({
    queryKey: ['mealPlans'],
    queryFn: mealPlans.list,
  })

  return (
    <PageLayout>
      <PageHeader
        title="Meal Plans"
        showBack
        backTo="/"
        actions={
          <Link
            to="/meals/new"
            className="p-2 text-lume-teal hover:opacity-80"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </Link>
        }
      />

      <div className="px-4 py-4 space-y-3">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : planList?.length === 0 ? (
          <EmptyState
            title="No meal plans yet"
            description="Create your first meal plan to assign to clients."
            action={
              <Link to="/meals/new" className="text-lume-teal font-medium mt-2 inline-block hover:opacity-80">
                Create your first meal plan
              </Link>
            }
          />
        ) : (
          planList?.map(plan => (
            <MealPlanCard key={plan.id} plan={plan} />
          ))
        )}
      </div>
    </PageLayout>
  )
}
